<template>
  <b-modal
    id="UserModal"
    class="modal"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    @hidden="doCancel"
  >
    <template
      slot="modal-header"
      slot-scope="{ close }"
    >
      <h5>{{ modalTitle }}</h5>
      <button
        class="btn btn--close"
        @click="close()"
      >
        <font-awesome-icon
          :icon="['fal', 'times']"
          class="icon alt"
        />
      </button>
    </template>

    <form-component style="min-height: 340px;">
      <form
        novalidate="novalidate"
        @submit.prevent
      >
        <input-text
          id="full_name"
          v-model="full_name"
          v-validate="'required'"
          name="full_name"
          data-vv-name="full_name"
          data-vv-as="Full name"
          label="Full Name"
          type="text"
          required
          :error="errors.first('full_name')"
        />

        <input-text
          id="email"
          v-model="email"
          v-validate="'required|email'"
          name="email"
          data-vv-name="email"
          data-vv-as="Email Address"
          label="Email Address"
          type="email"
          required
          :error="errors.first('email')"
        />

        <MazPhoneNumberInput
          :class="{'show-invalid': attemptedSave && !validPhoneNumber}"
          :default-country-code="phone_country_code"
          :default-phone-number="initial_phone"
          :success="false"
          :translations="{
            phoneNumberLabel: 'Mobile Phone Number',
          }"
          :translate="{phoneNumberLabel: 'Mobile Phone Number'}"
          @update="updatePhoneNumber($event)"
        />
        <p
          v-show="attemptedSave && !validPhoneNumber"
          class="form__error"
        >
          The Mobile Phone Number field is required and must be valid.
        </p>

        <input-select
          id="user_type"
          v-model="user_type"
          v-validate="'required'"
          name="user_type"
          data-vv-name="user_type"
          data-vv-as="User Type"
          label="User Type"
          :required="true"
          :error="errors.first('user_type')"
          :options="userTypes"
          @change="updateUserType()"
        />

        <input-select
          v-if="$store.getters.isKgbAdminUser
            && !(user_type === 'admin')
            && !(user_type === '')"
          id="organization_id"
          v-model="organization_id"
          v-validate="'required'"
          name="organization_id"
          data-vv-name="organization_id"
          data-vv-as="Organization"
          label="Organization"
          :required="true"
          :error="errors.first('organization_id')"
          :options="organizationList"
          :key-field="'id'"
          :display-field="'name'"
          @change="changedOrganization"
        />

        <input-select
          v-if="($store.getters.isAdminUser)
            && (user_type === 'read')
            && organizationAllowsDeleteFiles"
          id="allowed_to_remove_files"
          v-model="allowed_to_remove_files"
          v-validate="'required'"
          name="allowed_to_remove_files"
          data-vv-name="allowed_to_remove_files"
          data-vv-as="Allowed To Remove Files"
          label="Allowed To Remove Files"
          :required="true"
          :error="errors.first('allowed_to_remove_files')"
          :options="statusOptions"
        />

        <input-select
          v-if="user_type === 'admin'"
          id="allowed_to_access_management"
          v-model="allowed_to_access_management"
          v-validate="'required'"
          name="allowed_to_access_management"
          data-vv-name="allowed_to_access_management"
          data-vv-as="Allowed To Access Management"
          label="Allowed To Access Management"
          :required="true"
          :error="errors.first('allowed_to_access_management')"
          :options="statusOptions"
        />

        <input-select
          v-if="$store.getters.isKgbAdminUser &&
            (user_type === 'admin' || user_type === 'orgAdmin' || user_type === 'siteAdmin')"
          id="allowed_programmatic_access"
          v-model="allowed_programmatic_access"
          v-validate="'required'"
          name="allowed_programmatic_access"
          data-vv-name="allowed_programmatic_access"
          data-vv-as="Allowed Programmatic Access"
          label="Allowed Programmatic Access"
          :required="true"
          :error="errors.first('allowed_programmatic_access')"
          :options="statusOptions"
        />

        <div
          v-if="user_type && user_type !== 'admin' &&
            user_type !== 'orgAdmin'"
        >
          <div>
            <responsive-table>
              <thead class="inline-header">
                <tr>
                  <th>Member of site(s)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Primary</td>
                  <td />
                </tr>
                <tr
                  v-for="site in sites"
                  :key="site.id"
                >
                  <td>{{ site.name }}</td>
                  <td class="actions">
                    <button
                      v-if="($store.getters.isAdminUser
                        && organizationSites.some(x => x.id === site.id))
                        || $store.getters.isKgbAdminUser"
                      v-b-tooltip.hover
                      class="red-hover"
                      title="Delete"
                      type="button"
                      @click="deleteSite(site.id)"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'trash']"
                        class="icon alt"
                      />
                    </button>
                  </td>
                </tr>
                <tr v-if="$store.getters.isAdminUser && availableToAddOrgSites.length > 0">
                  <td colspan="2">
                    <input-select
                      id="addSiteSelect"
                      v-model="addSiteSelect"
                      name="addSiteSelect"
                      data-vv-name="addSiteSelect"
                      data-vv-as="Select Site to Add"
                      label="Select Site to Add"
                      :options="availableToAddOrgSites"
                      :key-field="'id'"
                      :display-field="'name'"
                      @change="addSite"
                    />
                  </td>
                </tr>
              </tbody>
            </responsive-table>
          </div>
        </div>
      </form>
    </form-component>

    <div v-if="show_public_programmatic_access_keys">
      <div v-if="loading_programmatic_access">
        <Loader />
      </div>
      <responsive-table v-else>
        <thead class="inline-header">
          <tr>
            <th>Access Keys</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="publicKey of publicKeys"
            :key="publicKey.id"
          >
            <td>
              <b>Id:</b> {{ publicKey.id }}
              <br>
              <b>Description:</b> {{ publicKey.description }}
              <br>
              <b>Last Accessed:</b> {{ publicKey.lastAccessed ?
                new Date(publicKey.lastAccessed).toLocaleString() : 'N/A' }}
            </td>
            <td class="actions">
              <button
                v-b-tooltip.hover
                class="red-hover"
                title="Delete"
                type="button"
                @click="deletePublicKey(publicKey)"
              >
                <font-awesome-icon
                  :icon="['fas', 'trash']"
                  class="icon alt"
                />
              </button>
            </td>
          </tr>
        </tbody>
      </responsive-table>
    </div>

    <template
      slot="modal-footer"
    >
      <button
        class="btn"
        type="button"
        @click="doCancel()"
      >
        Cancel
      </button>
      <button
        class="btn btn--primary"
        type="button"
        :disabled="saving"
        @click="save()"
      >
        <font-awesome-icon
          v-if="saving"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Save</span>
      </button>
    </template>
    <confirm-modal
      ref="confirmModal"
      :title="confirmTitle"
      :body="confirmBody"
      :on-confirm="confirmAction"
    />
  </b-modal>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import FormComponent from '@/components/FormComponent.vue';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';

export default {
  name: 'UserModal',
  components: {
    ResponsiveTable,
    ConfirmModal,
    FormComponent,
    InputText,
    InputSelect,
  },
  props: {
    doCancel: {
      type: Function,
    },
    getOrganizationSites: {
      type: Function,
    },
    doSave: {
      type: Function,
    },
    doDeletePublicKey: {
      type: Function,
    },
  },
  data: () => ({
    id: 0,
    loading_programmatic_access: true,
    full_name: '',
    phone_number: '',
    initial_phone: '',
    attemptedSave: false,
    phone_number_e164: '',
    phone_country_code: '',
    validPhoneNumber: false,
    email: '',
    user_type: '',
    allowed_to_remove_files: 'false',
    allowed_to_access_management: 'false',
    allowed_programmatic_access: 'false',
    show_public_programmatic_access_keys: false,
    confirmTitle: '',
    confirmBody: '',
    confirmAction: undefined,
    organization_id: undefined,
    saving: false,
    organizationList: [],
    publicKeys: [],
    initialSites: [],
    sites: [],
    addSiteSelect: '',
    organizationSites: [],
    userTypes: [
      { id: 'admin', display_label: 'Admin' },
      { id: 'orgAdmin', display_label: 'Organization Admin' },
      { id: 'siteAdmin', display_label: 'Site Admin' },
      { id: 'read', display_label: 'Read-Only' },
    ],
    statusOptions: [
      { id: 'true', display_label: 'Enabled' },
      { id: 'false', display_label: 'Disabled' },
    ],
  }),
  computed: {
    modalTitle() {
      return this.id > 0 ? 'Edit User' : 'New User';
    },
    organizationAllowsDeleteFiles() {
      if (!this.organization_id) {
        return false;
      }
      const org = this.organizationList.find(x => x.id === Number(this.organization_id));
      if (!org) {
        return false;
      }

      return org.allowed_to_remove_files;
    },
    availableToAddOrgSites() {
      const selectedSiteIds = this.sites.map(s => s.id);
      return this.organizationSites.filter(s => !selectedSiteIds.includes(s.id));
    },
  },
  methods: {
    deleteSite(siteId) {
      this.sites = this.sites.filter(site => site.id !== siteId);
    },
    populateOrganizationSitesFromService() {
      this.getOrganizationSites(this.organization_id).then((sites) => {
        this.organizationSites = sites;
      });
    },
    changedOrganization() {
      this.sites = [];
      this.populateOrganizationSitesFromService();
    },
    addSite() {
      const foundSite = this.organizationSites.find(s => s.id === Number(this.addSiteSelect));
      if (!foundSite) return;
      this.sites = [...this.sites, foundSite];
      this.addSiteSelect = '';
    },
    save() {
      this.attemptedSave = true;
      this.$validator.validateAll().then((result) => {
        if (result && this.validPhoneNumber) {
          this.errors.clear();
          this.saving = true;
          const isAdmin = this.user_type === 'admin';
          const isOrganizationAdmin = this.user_type === 'orgAdmin';
          const isSiteAdmin = this.user_type === 'siteAdmin';
          this.doSave(this.id, this.email, isAdmin,
            this.full_name, this.phone_number,
            this.phone_country_code, this.phone_number_e164,
            isOrganizationAdmin, isAdmin ? undefined : Number(this.organization_id),
            this.allowed_to_remove_files === 'true',
            this.allowed_to_access_management === 'true',
            this.allowed_programmatic_access === 'true', isSiteAdmin, this.sites.map(s => s.id),
            () => {
              this.saving = false;
            });
        }
      });
    },
    updatePhoneNumber(phoneEvent) {
      this.validPhoneNumber = phoneEvent.isValid;
      this.phone_number = phoneEvent.formatNational;
      this.phone_country_code = phoneEvent.countryCode;
      this.phone_number_e164 = phoneEvent.e164;
    },
    setModal(model, organizations) {
      this.attemptedSave = false;
      this.saving = false;
      this.loading_programmatic_access = true;
      this.id = model.id || 0;
      this.full_name = model.full_name || '';
      this.initial_phone = model.phone_number || '';
      this.phone_number = model.phone_number || '';
      this.phone_country_code = model.phone_country_code || '';
      this.phone_number_e164 = model.phone_number_e164 || '';
      this.validPhoneNumber = model.phone_number_e164 && model.phone_number_e164.length > 0;
      this.email = model.email || '';
      this.organizationList = organizations.filter(x => !x.is_removed) || [];
      this.user_type = '';
      this.organization_id = model.organization_id
        || this.$store.getters.currentUser.organization_id
        || null;
      this.userTypes = this.userTypes
        .filter(x => (this.$store.getters.isKgbAdminUser || x.id !== 'admin')
          && (this.$store.getters.isOrgAdminAccess || x.id !== 'orgAdmin'));
      this.allowed_to_remove_files = model.allowed_to_remove_files ? 'true' : 'false';
      this.allowed_to_access_management = model.allowed_to_access_management ? 'true' : 'false';
      this.allowed_programmatic_access = model.allowed_programmatic_access ? 'true' : 'false';
      this.show_public_programmatic_access_keys = model.allowed_programmatic_access === true;
      if (this.id) {
        if (model.is_admin) {
          this.user_type = 'admin';
        } else if (model.is_organization_admin) {
          this.user_type = 'orgAdmin';
        } else if (model.is_site_admin) {
          this.user_type = 'siteAdmin';
        } else {
          this.user_type = 'read';
        }
      }
      this.initialSites = model.sites || [];
      this.sites = [...this.initialSites];
      if (this.organization_id && this.$store.getters.isAdminUser) {
        this.populateOrganizationSitesFromService();
      }
    },
    setProgrammaticAccessPublicKeys(publicKeys) {
      this.publicKeys = publicKeys;
      this.loading_programmatic_access = false;
    },
    updateUserType() {
      if (['siteAdmin', 'read'].includes(this.user_type)
        && this.id === 0
      && this.organizationSites.length === 1) {
        this.sites = [...this.organizationSites];
      }
    },
    deletePublicKey(publicKey) {
      this.confirmTitle = `Confirm Delete Public Key ${publicKey.id}`;
      this.confirmBody = `Are you sure you want to delete public key ${publicKey.id}?
      Any application using this key and access key will no longer be able to login.`;
      this.confirmAction = (whenDone) => {
        this.$refs.confirmModal.hide();
        this.doDeletePublicKey(this.id, publicKey.id).then(() => {
          this.publicKeys = this.publicKeys.filter(key => key.id !== publicKey.id);
        });
        whenDone();
      };
      this.$refs.confirmModal.show();
    },
  },
};
</script>

<style scoped lang="scss">

</style>
